import React, { useEffect, useState } from 'react';
import Divider from '../../design-system/primitives/Divider';
import { Button } from '../ui/button';
import Eyebrow from '../../design-system/primitives/Eyebrow';
import Icon from '../../design-system/primitives/Icon';
import Segmented from '../../design-system/primitives/Segmented';
import { apiService } from '../../services/api';
import { useToast } from '../../context/ToastContext';

const ROLE_OPTIONS = [
  { value: 'view', label: 'Can view' },
  { value: 'edit', label: 'Can edit' },
  { value: 'manage_pipeline', label: 'Can manage pipeline' },
];

const ROLE_LABELS = {
  view: 'Can view',
  edit: 'Can edit',
  manage_pipeline: 'Can manage pipeline',
};

const inputStyle = {
  padding: '8px 10px', borderRadius: 8, background: 'var(--bg-elev-2)',
  border: '1px solid var(--line-soft)', color: 'var(--fg-strong)', fontSize: 13.5,
  fontFamily: 'var(--font-sans)', outline: 'none', width: '100%', boxSizing: 'border-box',
};

const captionStyle = { fontSize: 12.5, color: 'var(--fg-muted)', lineHeight: 1.45 };

const ShareAnalysisPanel = ({ analysisId, analysis, onClose }) => {
  const { toast } = useToast();
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emailPrefix, setEmailPrefix] = useState('');
  const [role, setRole] = useState('view');
  const [submitting, setSubmitting] = useState(false);

  const domain = (analysis?.user?.email || '').split('@')[1] || '';
  const email = emailPrefix.trim() ? `${emailPrefix.trim()}@${domain}` : '';

  useEffect(() => {
    if (!analysisId) return;
    apiService.getAnalysisShares(analysisId)
      .then((resp) => setShares(resp.data || []))
      .catch(() => toast('Failed to load current shares.', 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);

  const handleShare = () => {
    if (!email) return;
    setSubmitting(true);
    apiService.shareAnalysis(analysisId, { email, role })
      .then((resp) => {
        setShares((prev) => [resp.data, ...prev.filter((s) => s.id !== resp.data.id)]);
        setEmailPrefix('');
        toast(`Shared with ${resp.data.email}.`, 'success');
      })
      .catch((e) => toast(e.response?.data?.detail || 'Could not share this analysis.', 'error'))
      .finally(() => setSubmitting(false));
  };

  const handleRoleChange = (share, newRole) => {
    setShares((prev) => prev.map((s) => (s.id === share.id ? { ...s, role: newRole } : s)));
    apiService.updateAnalysisShare(analysisId, share.id, { role: newRole })
      .catch(() => {
        toast('Could not update that person\'s role.', 'error');
        setShares((prev) => prev.map((s) => (s.id === share.id ? share : s)));
      });
  };

  const handleRemove = (share) => {
    setShares((prev) => prev.filter((s) => s.id !== share.id));
    apiService.revokeAnalysisShare(analysisId, share.id)
      .catch(() => {
        toast('Could not remove that person.', 'error');
        setShares((prev) => [...prev, share]);
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Icon name="share-2" size={15} color="var(--accent)" />
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-strong)' }}>Share analysis</span>
      </div>
      <div style={captionStyle}>
        Sharing is only available with colleagues on your organization's email domain.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Eyebrow>Invite by email</Eyebrow>
        <div style={{ display: 'flex', alignItems: 'stretch' }}>
          <input
            type="text"
            placeholder="colleague"
            value={emailPrefix}
            onChange={(e) => setEmailPrefix(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleShare(); }}
            style={{
              ...inputStyle, borderTopRightRadius: 0, borderBottomRightRadius: 0,
              borderRight: 'none',
            }}
          />
          <span style={{
            ...inputStyle, width: 'auto', flexShrink: 0,
            borderTopLeftRadius: 0, borderBottomLeftRadius: 0,
            color: 'var(--fg-muted)', display: 'flex', alignItems: 'center',
            whiteSpace: 'nowrap',
          }}>
            @{domain}
          </span>
        </div>

        <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }}>
          <div style={captionStyle}>Each option includes all permissions to its left.</div>
          <Segmented options={ROLE_OPTIONS} value={role} onChange={setRole} />
        </div>

        <Button size="sm" onClick={handleShare} disabled={submitting || !email} style={{ marginTop: 10 }}>
          <Icon name="send" size={14} />
          Share
        </Button>
      </div>

      <Divider />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Eyebrow>People with access</Eyebrow>
        {loading ? (
          <div style={{ ...captionStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="loader" size={14} /> Loading…
          </div>
        ) : shares.length === 0 ? (
          <div style={captionStyle}>Not shared with anyone yet.</div>
        ) : (
          shares.map((share) => (
            <div
              key={share.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                borderRadius: 8, background: 'var(--bg-elev-2)',
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, color: 'var(--fg)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {share.name}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {share.email}
                </div>
              </div>
              <select
                value={share.role}
                onChange={(e) => handleRoleChange(share, e.target.value)}
                style={{ ...inputStyle, width: 'auto', padding: '4px 6px', fontSize: 12 }}
              >
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{ROLE_LABELS[opt.value]}</option>
                ))}
              </select>
              <button
                onClick={() => handleRemove(share)}
                title="Remove access"
                style={{ background: 'transparent', border: 'none', color: 'var(--fg-subtle)', cursor: 'pointer', padding: 4, display: 'inline-flex' }}
              >
                <Icon name="x" size={14} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ShareAnalysisPanel;
